import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  Box, Typography, Button, TextField, Stack, Alert, IconButton,
  Dialog, DialogTitle, DialogContent, DialogActions,
  MenuItem, Select, InputLabel, FormControl, Drawer, Card, CardContent,
  ToggleButtonGroup, ToggleButton, Tooltip, Chip,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import {
  ChevronLeft as ChevronLeftIcon,
  ChevronRight as ChevronRightIcon,
  Settings as SettingsIcon,
  CheckCircle as CheckCircleIcon,
  Event as EventIcon,
  Restaurant as RestaurantIcon,
} from '@mui/icons-material';
import { Calendar, dateFnsLocalizer, type View, Views } from 'react-big-calendar';
import { format, parse, startOfWeek, getDay, addDays, addWeeks, addMonths, subDays, subWeeks, subMonths, startOfDay, endOfDay, startOfMonth, endOfMonth, endOfWeek } from 'date-fns';
import enGB from 'date-fns/locale/en-GB';
import 'react-big-calendar/lib/css/react-big-calendar.css';

import { calendarApi, calendarSourcesApi, calendarConnectApi, driversApi, tenantAdminApi, dailyFocusBlocksApi, calendarConflictsApi } from '../api/services';
import type { CalendarEvent, CalendarSource, AnnualStrategicDriver, RestaurantResult, DailyFocusBlock, CalendarConflict } from '../types/models';
import RestaurantSearchDialog from '../components/RestaurantSearchDialog';
import ConnectCalendarCard from '../components/ConnectCalendarCard';
import CalendarSourcesCard from '../components/CalendarSourcesCard';
import CalendarEventDetail from '../components/CalendarEventDetail';
import { getCalendarStyles } from '../theme/calendarStyles';
import { getFYWeek, getFYLabel, getFYQuarter } from '../utils/financialYear';
import { colourForDriverId } from '../theme/tokens';
import { calculateFocusCoverage, minutesToHours } from '../utils/focusCoverage';
import HelpPanel from '../components/HelpPanel';

// ---------------------------------------------------------------------------
// date-fns localizer setup
// ---------------------------------------------------------------------------
const locales = { 'en-GB': enGB };
const localizer = dateFnsLocalizer({
  format,
  parse,
  startOfWeek: (date: Date) => startOfWeek(date, { weekStartsOn: 1 }),
  getDay,
  locales,
});

// ---------------------------------------------------------------------------
// Mapped event type for react-big-calendar
// ---------------------------------------------------------------------------
interface CalendarBigEvent {
  id: string;
  title: string;
  start: Date;
  end: Date;
  allDay: boolean;
  resource: CalendarEvent;
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

/** Compute the visible range for a given date + view.
 *  WEEK + DAY views pad an extra week back so the load-trending badge has
 *  last week's events to compare against without a second API round-trip.
 *  MONTH already covers ~5 weeks so no padding needed. */
function getVisibleRange(date: Date, view: View): { from: Date; to: Date } {
  switch (view) {
    case Views.DAY:
      return {
        from: startOfWeek(subWeeks(date, 1), { weekStartsOn: 1 }),
        to: endOfDay(date),
      };
    case Views.WEEK:
      return {
        from: startOfWeek(subWeeks(date, 1), { weekStartsOn: 1 }),
        to: endOfWeek(date, { weekStartsOn: 1 }),
      };
    case Views.MONTH: {
      // Month view also shows overflow weeks, so pad a week in each direction
      const ms = startOfMonth(date);
      const me = endOfMonth(date);
      return {
        from: startOfWeek(ms, { weekStartsOn: 1 }),
        to: endOfWeek(me, { weekStartsOn: 1 }),
      };
    }
    default:
      return { from: startOfDay(date), to: endOfDay(date) };
  }
}

function formatRangeLabel(date: Date, view: View): string {
  switch (view) {
    case Views.DAY:
      return format(date, 'EEEE, d MMMM yyyy');
    case Views.WEEK: {
      const ws = startOfWeek(date, { weekStartsOn: 1 });
      const we = endOfWeek(date, { weekStartsOn: 1 });
      if (ws.getMonth() === we.getMonth()) {
        return `${format(ws, 'd')} - ${format(we, 'd MMMM yyyy')}`;
      }
      if (ws.getFullYear() === we.getFullYear()) {
        return `${format(ws, 'd MMM')} - ${format(we, 'd MMM yyyy')}`;
      }
      return `${format(ws, 'd MMM yyyy')} - ${format(we, 'd MMM yyyy')}`;
    }
    case Views.MONTH:
      return format(date, 'MMMM yyyy');
    default:
      return format(date, 'd MMMM yyyy');
  }
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------
export default function CalendarPage() {
  const theme = useTheme();
  const calendarSx = useMemo(() => getCalendarStyles(theme), [theme]);

  // Data state
  const [events, setEvents] = useState<CalendarEvent[]>([]);
  const [loading, setLoading] = useState(false);
  const [activeDrivers, setActiveDrivers] = useState<AnnualStrategicDriver[]>([]);

  // Calendar navigation
  const [currentDate, setCurrentDate] = useState<Date>(new Date());
  const [currentView, setCurrentView] = useState<View>(Views.WEEK);

  // Drawer
  const [drawerOpen, setDrawerOpen] = useState(false);

  // Sync / connection
  const [syncMessage, setSyncMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);
  const [connectionBanner, setConnectionBanner] = useState<string | null>(null);

  // Create Event state
  const [createEventOpen, setCreateEventOpen] = useState(false);
  const [oauthSources, setOauthSources] = useState<CalendarSource[]>([]);
  const [createEventData, setCreateEventData] = useState({
    title: '',
    startTime: '',
    endTime: '',
    location: '',
    description: '',
    eventType: 'Meeting',
    attendees: '',
    sourceId: '',
  });
  const [creatingEvent, setCreatingEvent] = useState(false);
  const [createEventMessage, setCreateEventMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);

  // Restaurant search state
  const [restaurantDialogOpen, setRestaurantDialogOpen] = useState(false);

  // Event detail state
  const [selectedEvent, setSelectedEvent] = useState<CalendarEvent | null>(null);
  const [eventDetailOpen, setEventDetailOpen] = useState(false);

  // Focus blocks for the visible date range (for conflict highlighting)
  const [focusBlocks, setFocusBlocks] = useState<DailyFocusBlock[]>([]);

  // Calendar conflicts
  const [calendarConflicts, setCalendarConflicts] = useState<CalendarConflict[]>([]);

  // Financial Year state
  const [fyEnabled, setFyEnabled] = useState(false);
  const [fyStartMonth, setFyStartMonth] = useState(1);

  // -------------------------------------------------------------------------
  // Data loading
  // -------------------------------------------------------------------------

  const loadEventsForRange = useCallback(async (from: Date, to: Date) => {
    setLoading(true);
    try {
      const fromStr = format(from, 'yyyy-MM-dd');
      const toStr = format(to, 'yyyy-MM-dd');
      // Show cached events immediately
      setEvents(await calendarApi.getByRange(fromStr, toStr));
      // Trigger a background sync for this range so future events populate
      calendarApi.sync({ from: fromStr, to: toStr })
        .then(() => calendarApi.getByRange(fromStr, toStr).then(setEvents))
        .catch(() => {});
    } catch (err) {
      console.error('Failed to load events', err);
    } finally {
      setLoading(false);
    }
  }, []);

  const loadCurrentRange = useCallback(() => {
    const { from, to } = getVisibleRange(currentDate, currentView);
    loadEventsForRange(from, to);
  }, [currentDate, currentView, loadEventsForRange]);

  // Refetch events whenever the user navigates (date or view change). The custom
  // arrow buttons call setCurrentDate directly, which doesn't fire react-big-calendar's
  // onRangeChange — relying on that callback alone left next-week navigation showing
  // a blank grid. This effect also covers the initial mount load.
  useEffect(() => {
    loadCurrentRange();
  }, [loadCurrentRange]);

  // Load strategic drivers on mount
  useEffect(() => {
    driversApi.getActive()
      .then(setActiveDrivers)
      .catch(() => {});
  }, []);

  // Refresh calendar when background sync completes
  useEffect(() => {
    const handleSync = () => loadCurrentRange();
    window.addEventListener('cadence:calendar-synced', handleSync);
    return () => window.removeEventListener('cadence:calendar-synced', handleSync);
  }, [loadCurrentRange]);

  // Load DailyFocusBlocks for the visible range (for conflict highlighting)
  useEffect(() => {
    const { from, to } = getVisibleRange(currentDate, currentView);
    const fromStr = format(from, 'yyyy-MM-dd');
    const toStr = format(to, 'yyyy-MM-dd');
    dailyFocusBlocksApi.getByRange(fromStr, toStr).then(setFocusBlocks).catch(() => {});
  }, [currentDate, currentView]);

  // Load calendar conflicts for the current date (day view) or week
  useEffect(() => {
    const dateStr = format(currentDate, 'yyyy-MM-dd');
    calendarConflictsApi.getByDate(dateStr).then(setCalendarConflicts).catch(() => setCalendarConflicts([]));
  }, [currentDate]);

  // Load FY settings on mount
  useEffect(() => {
    tenantAdminApi.getFinancialYear().then((fy: any) => {
      setFyEnabled(fy.useFinancialYear);
      setFyStartMonth(fy.useFinancialYear ? fy.financialYearStartMonth : 1);
    }).catch(() => {});
  }, []);

  // Load OAuth sources and auto-sync connected calendars + ICS on page load
  useEffect(() => {
    const loadAndSync = async () => {
      try {
        const all = await calendarSourcesApi.getAll();
        const oauth = all.filter((s: CalendarSource) => s.sourceType === 'Microsoft365' || s.sourceType === 'Google');
        setOauthSources(oauth);
        if (oauth.length > 0 && !createEventData.sourceId) {
          setCreateEventData(prev => ({ ...prev, sourceId: oauth[0].id }));
        }

        // Auto-sync all connected OAuth sources in background
        const connected = oauth.filter((s: CalendarSource) => s.isConnected && !s.needsReconnect);
        for (const source of connected) {
          calendarConnectApi.sync(source.id).catch(() => {});
        }

        // Also sync ICS calendar from preferences (if configured)
        calendarApi.sync().catch(() => {});

        // Reload events after syncs complete
        setTimeout(() => loadCurrentRange(), 2000);
      } catch {
        // Silently fail -- no OAuth sources available
        // Still attempt ICS sync in case preferences have an ICS URL
        calendarApi.sync().catch(() => {});
        setTimeout(() => loadCurrentRange(), 2000);
      }
    };
    loadAndSync();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Check for ?connected= query param on mount
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const connected = params.get('connected');
    if (connected === 'microsoft' || connected === 'google') {
      const label = connected === 'microsoft' ? 'Microsoft 365' : 'Google Calendar';
      setConnectionBanner(`${label} connected successfully! Your calendar will sync automatically.`);
      window.history.replaceState({}, '', window.location.pathname);
    }
  }, []);

  // -------------------------------------------------------------------------
  // Event mapping for react-big-calendar
  // -------------------------------------------------------------------------

  const driverMap = useMemo(() => {
    const map = new Map<string, string>();
    activeDrivers.forEach(d => map.set(d.id, d.title));
    return map;
  }, [activeDrivers]);

  const mappedEvents: CalendarBigEvent[] = useMemo(() =>
    events.map(event => ({
      id: event.id,
      title: event.strategicDriverId && driverMap.has(event.strategicDriverId)
        ? `\u{1F3AF} ${event.title}`
        : event.title,
      start: new Date(event.startTime),
      end: new Date(event.endTime),
      allDay: event.isAllDay,
      resource: event,
    })),
    [events, driverMap],
  );

  // FY info for the current date
  const fyInfo = useMemo(() => {
    if (!fyEnabled) return null;
    const label = getFYLabel(currentDate, fyStartMonth);
    const quarter = getFYQuarter(currentDate, fyStartMonth);
    const week = getFYWeek(currentDate, fyStartMonth);
    return { label, quarter, week };
  }, [currentDate, fyEnabled, fyStartMonth]);

  // -------------------------------------------------------------------------
  // Navigation handlers
  // -------------------------------------------------------------------------

  const handleNavigateBack = () => {
    setCurrentDate(prev => {
      switch (currentView) {
        case Views.DAY: return subDays(prev, 1);
        case Views.WEEK: return subWeeks(prev, 1);
        case Views.MONTH: return subMonths(prev, 1);
        default: return prev;
      }
    });
  };

  const handleNavigateForward = () => {
    setCurrentDate(prev => {
      switch (currentView) {
        case Views.DAY: return addDays(prev, 1);
        case Views.WEEK: return addWeeks(prev, 1);
        case Views.MONTH: return addMonths(prev, 1);
        default: return prev;
      }
    });
  };

  const handleNavigateToday = () => {
    setCurrentDate(new Date());
  };

  const handleViewChange = (_: React.MouseEvent<HTMLElement>, newView: View | null) => {
    if (newView) setCurrentView(newView);
  };

  // -------------------------------------------------------------------------
  // react-big-calendar callbacks
  // -------------------------------------------------------------------------

  const handleRangeChange = useCallback((range: Date[] | { start: Date; end: Date }) => {
    if (Array.isArray(range)) {
      if (range.length > 0) {
        // Update currentDate to match the visible range midpoint (fixes header date sync)
        const mid = new Date((range[0].getTime() + range[range.length - 1].getTime()) / 2);
        setCurrentDate(mid);
        loadEventsForRange(startOfDay(range[0]), endOfDay(range[range.length - 1]));
      }
    } else {
      const mid = new Date((range.start.getTime() + range.end.getTime()) / 2);
      setCurrentDate(mid);
      loadEventsForRange(range.start, range.end);
    }
  }, [loadEventsForRange]);

  const handleSelectEvent = useCallback((event: CalendarBigEvent) => {
    setSelectedEvent(event.resource);
    setEventDetailOpen(true);
  }, []);

  // Weekly focus-coverage band. The band always covers the ISO week that
  // contains the currently-selected date — so day/week/month views all share
  // the same "this week" framing at the top. Meetings come from the events
  // list; focus comes from the DailyFocusBlock rows already loaded for the
  // visible range.
  const weeklyCoverage = useMemo(() => {
    const weekStartDate = startOfWeek(currentDate, { weekStartsOn: 1 });
    const weekEndDate = endOfWeek(currentDate, { weekStartsOn: 1 });
    return calculateFocusCoverage(
      events.filter((e) => {
        const d = new Date(e.startTime);
        return d >= weekStartDate && d <= weekEndDate;
      }),
      focusBlocks.filter((b) => {
        const d = new Date(b.date);
        return d >= weekStartDate && d <= weekEndDate;
      }),
      weekStartDate,
      weekEndDate,
    );
  }, [events, focusBlocks, currentDate]);

  // Previous week's coverage — pulled from the same `events` array because
  // getVisibleRange now pads a week back for DAY / WEEK views. Used to
  // surface a "vs last week" delta on the trend badge so the executive
  // sees workload trajectory at a glance (User-Side Review §3.3).
  const lastWeekCoverage = useMemo(() => {
    const lastWeekStart = startOfWeek(subWeeks(currentDate, 1), { weekStartsOn: 1 });
    const lastWeekEnd = endOfWeek(subWeeks(currentDate, 1), { weekStartsOn: 1 });
    return calculateFocusCoverage(
      events.filter((e) => {
        const d = new Date(e.startTime);
        return d >= lastWeekStart && d <= lastWeekEnd;
      }),
      focusBlocks.filter((b) => {
        const d = new Date(b.date);
        return d >= lastWeekStart && d <= lastWeekEnd;
      }),
      lastWeekStart,
      lastWeekEnd,
    );
  }, [events, focusBlocks, currentDate]);

  // Meeting-load trend: rounded delta in hours, with a label that's only
  // shown when last week had any meetings (otherwise comparison is noise).
  const meetingTrend = useMemo(() => {
    const last = lastWeekCoverage.meetingMinutes;
    if (last < 30) return null; // <30min last week — comparison meaningless
    const deltaMin = weeklyCoverage.meetingMinutes - last;
    const deltaHrs = Math.round(deltaMin / 60 * 10) / 10;
    if (Math.abs(deltaHrs) < 0.5) return { kind: 'same' as const };
    return {
      kind: deltaHrs > 0 ? ('up' as const) : ('down' as const),
      hours: Math.abs(deltaHrs),
    };
  }, [weeklyCoverage.meetingMinutes, lastWeekCoverage.meetingMinutes]);

  const eventPropGetter = useCallback((event: CalendarBigEvent) => {
    const evt = event.resource as CalendarEvent;

    // Base fill: priority / all-day overrides pull the chosen colour; then
    // a linked strategic driver overrides the base so at a glance the week
    // reads as "how much of my time is landing on which driver?". Events
    // with no driver fall back to the neutral primary. Priority and
    // all-day tags still win over driver since they convey urgency /
    // structure that a driver match alone does not.
    let backgroundColor = theme.palette.primary.main;
    let borderColor = theme.palette.primary.main;
    if (evt.isPriorityMeeting || evt.isPrivate) {
      backgroundColor = theme.palette.error.main;
      borderColor = theme.palette.error.main;
    } else if (evt.isAllDay) {
      backgroundColor = theme.palette.warning.main;
      borderColor = theme.palette.warning.main;
    } else if (evt.strategicDriverId) {
      backgroundColor = colourForDriverId(evt.strategicDriverId);
      borderColor = backgroundColor;
    }

    // Check if this event conflicts with an allocated focus block (date-specific)
    const eventDateStr = format(event.start, 'yyyy-MM-dd');
    const eventStartMin = event.start.getHours() * 60 + event.start.getMinutes();
    const eventEndMin = event.end.getHours() * 60 + event.end.getMinutes();
    const conflictsWithFocus = focusBlocks.some(fb => {
      if (fb.date !== eventDateStr) return false;
      const fbStartMin = parseInt(fb.startTime.split(':')[0]) * 60 + parseInt(fb.startTime.split(':')[1]);
      const fbEndMin = parseInt(fb.endTime.split(':')[0]) * 60 + parseInt(fb.endTime.split(':')[1]);
      return eventStartMin < fbEndMin && eventEndMin > fbStartMin;
    });

    const hasDriver = !!evt.strategicDriverId;

    // Check if this event has a hard conflict (double-booked or work block overlap)
    const hasHardConflict = calendarConflicts.some(
      c => c.severity === 'Hard' && (c.calendarEventId === evt.id || c.conflictingEventId === evt.id)
    );

    return {
      style: {
        backgroundColor,
        borderColor,
        borderRadius: 4,
        opacity: conflictsWithFocus ? 0.8 : 0.9,
        color: '#fff',
        border: 'none',
        borderLeft: hasHardConflict
          ? '3px solid #D32F2F'
          : conflictsWithFocus
            ? '3px solid #FF5252'
            : hasDriver
              ? `3px solid ${theme.palette.success.main}`
              : 'none',
        borderRight: hasHardConflict ? '3px solid #D32F2F' : 'none',
        fontSize: '0.8rem',
      },
    };
  }, [theme, focusBlocks, calendarConflicts]);

  // -------------------------------------------------------------------------
  // Restaurant select handler
  // -------------------------------------------------------------------------

  const handleRestaurantSelect = (restaurant: RestaurantResult) => {
    setCreateEventData(prev => ({
      ...prev,
      title: `Dinner at ${restaurant.name}`,
      location: restaurant.address || restaurant.name,
      description: [
        restaurant.name,
        restaurant.rating ? `Rating: ${restaurant.rating}/5` : null,
        restaurant.address,
        restaurant.googleMapsUrl ? `Google Maps: ${restaurant.googleMapsUrl}` : null,
      ].filter(Boolean).join('\n'),
    }));
    setCreateEventOpen(true);
  };

  // -------------------------------------------------------------------------
  // Create event handler
  // -------------------------------------------------------------------------

  const handleCreateEvent = async () => {
    if (!createEventData.title.trim() || !createEventData.startTime || !createEventData.endTime || !createEventData.sourceId) return;
    setCreatingEvent(true);
    setCreateEventMessage(null);
    try {
      await calendarSourcesApi.createEvent(createEventData.sourceId, {
        title: createEventData.title,
        startTime: createEventData.startTime,
        endTime: createEventData.endTime,
        location: createEventData.location || undefined,
        description: createEventData.description || undefined,
        eventType: createEventData.eventType,
        attendees: createEventData.attendees
          ? createEventData.attendees.split(',').map(a => a.trim()).filter(Boolean)
          : undefined,
      });
      setCreateEventOpen(false);
      setCreateEventData({
        title: '',
        startTime: '',
        endTime: '',
        location: '',
        description: '',
        eventType: 'Meeting',
        attendees: '',
        sourceId: oauthSources.length > 0 ? oauthSources[0].id : '',
      });
      setCreateEventMessage({ type: 'success', text: 'Event created successfully. Refreshing calendar...' });
      loadCurrentRange();
    } catch (err: any) {
      const msg = err.response?.data?.error || err.response?.data?.message || err.message || 'Failed to create event';
      setCreateEventMessage({ type: 'error', text: typeof msg === 'string' ? msg : 'Failed to create event' });
    } finally {
      setCreatingEvent(false);
    }
  };

  // -------------------------------------------------------------------------
  // Stats (for drawer)
  // -------------------------------------------------------------------------

  const meetingMinutes = events
    .filter(e => !e.isAllDay)
    .reduce((sum, e) => sum + (new Date(e.endTime).getTime() - new Date(e.startTime).getTime()) / 60000, 0);

  const priorityMeetingCount = events.filter(e => e.isPriorityMeeting || e.isPrivate).length;

  // -------------------------------------------------------------------------
  // Render
  // -------------------------------------------------------------------------

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: 'calc(100vh - 80px)' }}>
      {/* Header bar */}
      <Box sx={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        mb: 2,
        flexWrap: 'wrap',
        gap: 1,
      }}>
        {/* Left: Title */}
        <Typography variant="h4" sx={{ mr: 2 }}>Calendar</Typography>

        {/* Centre: Date navigation */}
        <Stack direction="row" spacing={1} alignItems="center" sx={{ flexGrow: 1, justifyContent: 'center' }}>
          <IconButton size="small" onClick={handleNavigateBack} aria-label="Previous">
            <ChevronLeftIcon />
          </IconButton>
          <Button size="small" variant="outlined" onClick={handleNavigateToday}>
            Today
          </Button>
          <IconButton size="small" onClick={handleNavigateForward} aria-label="Next">
            <ChevronRightIcon />
          </IconButton>
          <Typography variant="h6" sx={{ mx: 1, whiteSpace: 'nowrap', fontWeight: 500 }}>
            {formatRangeLabel(currentDate, currentView)}
          </Typography>
          {fyInfo && (
            <Typography variant="body2" sx={{ color: 'text.secondary', whiteSpace: 'nowrap', ml: 1 }}>
              {fyInfo.label} &middot; Q{fyInfo.quarter} &middot; Week {fyInfo.week}
            </Typography>
          )}
        </Stack>

        {/* Right: View toggle + actions + settings */}
        <Stack direction="row" spacing={1} alignItems="center">
          <ToggleButtonGroup
            value={currentView}
            exclusive
            onChange={handleViewChange}
            size="small"
          >
            <ToggleButton value={Views.DAY}>Day</ToggleButton>
            <ToggleButton value={Views.WEEK}>Week</ToggleButton>
            <ToggleButton value={Views.MONTH}>Month</ToggleButton>
          </ToggleButtonGroup>

          <Button
            variant="contained"
            size="small"
            startIcon={<EventIcon />}
            onClick={() => setCreateEventOpen(true)}
            disabled={oauthSources.length === 0}
            title={oauthSources.length === 0 ? 'Connect a Microsoft 365 or Google calendar to create events' : 'Create a new calendar event'}
          >
            Create Event
          </Button>
          <Button
            variant="outlined"
            size="small"
            startIcon={<RestaurantIcon />}
            onClick={() => setRestaurantDialogOpen(true)}
          >
            Book Restaurant
          </Button>

          <IconButton onClick={() => setDrawerOpen(true)} aria-label="Calendar settings">
            <SettingsIcon />
          </IconButton>
        </Stack>
      </Box>

      <HelpPanel title="About Your Calendar" storageKey="calendar">
        Calendar syncs from Microsoft 365 or Google. Link events to strategic drivers for time tracking.
      </HelpPanel>

      {/* Weekly focus-coverage band. Baseline is 40h; if the sum of meetings
          and focus tips over 100% the band flips to a warning palette and
          says so, since that's the "there is no protected time left this
          week" story the recommendation wants surfaced. */}
      <Box sx={{ mb: 1.5, p: 1.25, borderRadius: 1, bgcolor: 'background.paper', border: 1, borderColor: 'divider' }}>
        <Stack direction={{ xs: 'column', md: 'row' }} spacing={{ xs: 1, md: 3 }} alignItems={{ xs: 'flex-start', md: 'center' }}>
          <Typography variant="body2" fontWeight={600} sx={{ minWidth: 120 }}>
            This week
          </Typography>
          <Box sx={{ flex: 1, width: '100%' }}>
            <Box sx={{ display: 'flex', height: 10, borderRadius: 1, overflow: 'hidden', bgcolor: 'action.hover' }}>
              <Box sx={{
                width: `${Math.min(weeklyCoverage.meetingPercent, 100)}%`,
                bgcolor: weeklyCoverage.overloaded ? 'error.main' : 'primary.main',
              }} />
              <Box sx={{
                width: `${Math.min(weeklyCoverage.focusPercent, Math.max(0, 100 - weeklyCoverage.meetingPercent))}%`,
                bgcolor: 'success.main',
              }} />
            </Box>
          </Box>
          <Stack direction="row" spacing={2} flexWrap="wrap" useFlexGap alignItems="center">
            <Stack direction="row" spacing={0.5} alignItems="center">
              <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                <strong>{minutesToHours(weeklyCoverage.meetingMinutes)}</strong> meetings
              </Typography>
              {meetingTrend && (
                <Tooltip
                  title={
                    meetingTrend.kind === 'same'
                      ? 'About the same as last week'
                      : `${meetingTrend.hours}h ${meetingTrend.kind === 'up' ? 'more' : 'less'} than last week (${minutesToHours(lastWeekCoverage.meetingMinutes)})`
                  }
                  placement="top"
                >
                  <Chip
                    size="small"
                    label={
                      meetingTrend.kind === 'same'
                        ? '≈ last week'
                        : `${meetingTrend.kind === 'up' ? '↑' : '↓'} ${meetingTrend.hours}h vs last week`
                    }
                    sx={{
                      height: 18,
                      fontSize: '0.65rem',
                      bgcolor:
                        meetingTrend.kind === 'up' ? 'warning.light'
                        : meetingTrend.kind === 'down' ? 'success.light'
                        : 'action.hover',
                      color:
                        meetingTrend.kind === 'up' ? 'warning.dark'
                        : meetingTrend.kind === 'down' ? 'success.dark'
                        : 'text.secondary',
                      fontWeight: 500,
                      cursor: 'help',
                    }}
                  />
                </Tooltip>
              )}
            </Stack>
            <Typography variant="caption" sx={{ color: 'text.secondary' }}>
              <strong>{minutesToHours(weeklyCoverage.focusMinutes)}</strong> focus
            </Typography>
            <Typography variant="caption" sx={{ color: weeklyCoverage.overloaded ? 'error.main' : 'text.secondary', fontWeight: weeklyCoverage.overloaded ? 600 : 400 }}>
              {weeklyCoverage.overloaded
                ? 'Week over baseline'
                : <><strong>{minutesToHours(weeklyCoverage.freeMinutes)}</strong> unallocated</>}
            </Typography>
          </Stack>
        </Stack>
      </Box>

      {/* Banners / alerts */}
      {connectionBanner && (
        <Alert severity="success" sx={{ mb: 1 }} icon={<CheckCircleIcon />} onClose={() => setConnectionBanner(null)}>
          {connectionBanner}
        </Alert>
      )}
      {syncMessage && (
        <Alert severity={syncMessage.type} sx={{ mb: 1 }} onClose={() => setSyncMessage(null)}>
          {syncMessage.text}
        </Alert>
      )}
      {createEventMessage && (
        <Alert severity={createEventMessage.type} sx={{ mb: 1 }} onClose={() => setCreateEventMessage(null)}>
          {createEventMessage.text}
        </Alert>
      )}

      {/* Calendar */}
      <Box sx={{ flexGrow: 1, minHeight: 0, ...calendarSx }}>
        <Calendar<CalendarBigEvent>
          localizer={localizer}
          events={mappedEvents}
          date={currentDate}
          view={currentView}
          onNavigate={setCurrentDate}
          onView={setCurrentView}
          onRangeChange={handleRangeChange}
          onSelectEvent={handleSelectEvent}
          eventPropGetter={eventPropGetter}
          style={{ height: '100%' }}
          views={[Views.DAY, Views.WEEK, Views.MONTH]}
          toolbar={false}
          popup
          selectable={true}
          onSelectSlot={(slotInfo) => {
            setCreateEventData(prev => ({
              ...prev,
              title: '',
              startTime: slotInfo.start.toISOString().slice(0, 16),
              endTime: slotInfo.end.toISOString().slice(0, 16),
            }));
            setCreateEventOpen(true);
          }}
          scrollToTime={new Date(1970, 0, 1, 7, 0, 0)}
        />
      </Box>

      {/* Settings Drawer */}
      <Drawer
        anchor="right"
        open={drawerOpen}
        onClose={() => setDrawerOpen(false)}
        PaperProps={{ sx: { width: { xs: '100%', sm: 420 }, maxWidth: '100vw', p: 2 } }}
      >
        <Typography variant="h6" sx={{ mb: 2 }}>Calendar Settings</Typography>

        <ConnectCalendarCard />

        <Box sx={{ mt: 2 }}>
          <CalendarSourcesCard />
        </Box>

        <Card sx={{ mt: 2 }}>
          <CardContent>
            <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1.5 }}>
              Current View Stats
            </Typography>
            <Stack spacing={1.5}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Typography variant="body2" color="text.secondary">Events</Typography>
                <Typography variant="h6">{events.length}</Typography>
              </Box>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Typography variant="body2" color="text.secondary">Meeting Time</Typography>
                <Typography variant="h6">{Math.round(meetingMinutes / 60 * 10) / 10}h</Typography>
              </Box>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Typography variant="body2" color="text.secondary">Priority Meetings</Typography>
                <Typography variant="h6" color="error.main">{priorityMeetingCount}</Typography>
              </Box>
            </Stack>
          </CardContent>
        </Card>
      </Drawer>

      {/* Event Detail Dialog */}
      <CalendarEventDetail
        event={selectedEvent}
        open={eventDetailOpen}
        onClose={() => {
          setEventDetailOpen(false);
          setSelectedEvent(null);
        }}
        onDriverLinked={loadCurrentRange}
        drivers={activeDrivers}
      />

      {/* Create Event Dialog */}
      <Dialog open={createEventOpen} onClose={() => setCreateEventOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Create Calendar Event</DialogTitle>
        <DialogContent>
          <Stack spacing={2.5} sx={{ mt: 1 }}>
            <FormControl fullWidth size="small">
              <InputLabel>Calendar</InputLabel>
              <Select
                value={createEventData.sourceId}
                label="Calendar"
                onChange={e => setCreateEventData(prev => ({ ...prev, sourceId: e.target.value }))}
              >
                {oauthSources.map(source => (
                  <MenuItem key={source.id} value={source.id}>
                    {source.name} ({source.sourceType === 'Microsoft365' ? 'Microsoft 365' : 'Google'})
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <TextField
              label="Title"
              fullWidth
              size="small"
              value={createEventData.title}
              onChange={e => setCreateEventData(prev => ({ ...prev, title: e.target.value }))}
              placeholder="Meeting title"
            />
            <Stack direction="row" spacing={2}>
              <TextField
                label="Start"
                type="datetime-local"
                fullWidth
                size="small"
                value={createEventData.startTime}
                onChange={e => setCreateEventData(prev => ({ ...prev, startTime: e.target.value }))}
                InputLabelProps={{ shrink: true }}
              />
              <TextField
                label="End"
                type="datetime-local"
                fullWidth
                size="small"
                value={createEventData.endTime}
                onChange={e => setCreateEventData(prev => ({ ...prev, endTime: e.target.value }))}
                InputLabelProps={{ shrink: true }}
              />
            </Stack>
            <TextField
              label="Location"
              fullWidth
              size="small"
              value={createEventData.location}
              onChange={e => setCreateEventData(prev => ({ ...prev, location: e.target.value }))}
              placeholder="Office, Zoom link, etc."
            />
            <TextField
              label="Description"
              fullWidth
              size="small"
              multiline
              rows={3}
              value={createEventData.description}
              onChange={e => setCreateEventData(prev => ({ ...prev, description: e.target.value }))}
              placeholder="Event description..."
            />
            <FormControl fullWidth size="small">
              <InputLabel>Event Type</InputLabel>
              <Select
                value={createEventData.eventType}
                label="Event Type"
                onChange={e => setCreateEventData(prev => ({ ...prev, eventType: e.target.value }))}
              >
                <MenuItem value="Meeting">Meeting</MenuItem>
                <MenuItem value="FocusTime">Focus Time</MenuItem>
                <MenuItem value="Travel">Travel</MenuItem>
                <MenuItem value="Reservation">Reservation</MenuItem>
                <MenuItem value="Personal">Personal</MenuItem>
              </Select>
            </FormControl>
            <TextField
              label="Attendees"
              fullWidth
              size="small"
              value={createEventData.attendees}
              onChange={e => setCreateEventData(prev => ({ ...prev, attendees: e.target.value }))}
              placeholder="email1@example.com, email2@example.com"
              helperText="Comma-separated email addresses"
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCreateEventOpen(false)}>Cancel</Button>
          <Button
            variant="contained"
            onClick={handleCreateEvent}
            disabled={creatingEvent || !createEventData.title.trim() || !createEventData.startTime || !createEventData.endTime || !createEventData.sourceId}
          >
            {creatingEvent ? 'Creating...' : 'Create Event'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Restaurant Search Dialog */}
      <RestaurantSearchDialog
        open={restaurantDialogOpen}
        onClose={() => setRestaurantDialogOpen(false)}
        onSelect={handleRestaurantSelect}
      />
    </Box>
  );
}
